/**
 * Main Application Component
 * 
 * Root component that orchestrates file upload, data parsing, pagination,
 * and rendering of the lead sheet viewer.
 */

import React, { useEffect } from 'react';
import { WelcomeScreen } from './components/WelcomeScreen';
import { Page } from './components/Page';
import { useFileUpload } from './hooks/useFileUpload';
import { usePagination } from './hooks/usePagination';
import { useSwipeNavigation } from './hooks/useSwipeNavigation';
import { useMusicianConfig } from './hooks/useMusicianConfig';
import { groupRegionsByPage, calculateTotalPages } from './utils/regionLayout';

/**
 * Main application component
 */
export function App() {
  const {
    projectData,
    isLoading,
    error,
    fileInputRef,
    triggerUpload,
    onInputChange,
    onFileDrop,
  } = useFileUpload();
  
  const { updateMusicianById } = useMusicianConfig();
  
  // Calculate pagination info
  const totalPages = projectData ? calculateTotalPages(projectData.regions) : 1;
  const pageGroups = projectData ? groupRegionsByPage(projectData.regions) : [];
  
  const {
    currentPage,
    canGoNext,
    canGoPrevious,
    goToNextPage,
    goToPreviousPage,
    resetToFirstPage,
  } = usePagination(totalPages);
  
  // Swipe navigation handlers
  const handleSwipeLeft = () => {
    if (canGoNext) goToNextPage();
  };
  
  const handleSwipeRight = () => {
    if (canGoPrevious) goToPreviousPage();
  };
  
  const touchHandlers = useSwipeNavigation(handleSwipeLeft, handleSwipeRight);
  
  // Reset to first page when a new file is loaded
  useEffect(() => {
    if (projectData) {
      resetToFirstPage();
    }
  }, [projectData, resetToFirstPage]);
  
  // Update musician context when project data changes
  useEffect(() => {
    if (projectData?.musicianId) {
      updateMusicianById(projectData.musicianId);
    }
  }, [projectData, updateMusicianById]);
  
  // Show loading state
  if (isLoading) {
    return (
      <div style={{ color: 'white', background: 'black', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Loading project...</p>
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div style={{ color: 'red', background: 'black', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: '20px' }}>
        <h2>Error Loading Project</h2>
        <p>{error}</p>
        <button onClick={triggerUpload} style={{ marginTop: '20px', padding: '10px 20px' }}>
          Try Again
        </button>
      </div>
    );
  }
  
  // Show welcome screen if no project loaded
  if (!projectData) {
    return (
      <WelcomeScreen
        onUploadClick={triggerUpload}
        onFileChange={onInputChange}
        onFileDrop={onFileDrop}
        inputRef={fileInputRef}
      />
    );
  }
  
  // Show main page view
  const currentRegions = pageGroups[currentPage - 1] || [];
  
  return (
    <Page
      projectData={projectData}
      regions={currentRegions}
      currentPage={currentPage}
      totalPages={totalPages}
      canGoNext={canGoNext}
      canGoPrevious={canGoPrevious}
      onNextPage={goToNextPage}
      onPreviousPage={goToPreviousPage}
      onUploadClick={triggerUpload}
      onFileChange={onInputChange}
      inputRef={fileInputRef}
      touchHandlers={touchHandlers}
    />
  );
}


